import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Trash2, Mail } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const fmt = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("ro-RO", { dateStyle: "medium" });
  } catch {
    return "";
  }
};

export const AdminSubscribersPage = () => {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/subscribers")
      .then((r) => setSubs(r.data))
      .catch((e) => toast.error(formatApiError(e, "Nu am putut încărca abonații")))
      .finally(() => setLoading(false));
  }, []);

  const remove = async (id) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm("Ștergi acest abonat? Nu va mai primi emailuri.")) return;
    try {
      await api.delete(`/admin/subscribers/${id}`);
      setSubs((all) => all.filter((s) => s.id !== id));
      toast.success("Abonat șters");
    } catch (e) {
      toast.error(formatApiError(e, "Nu am putut șterge abonatul"));
    }
  };

  const active = subs.filter((s) => s.active !== false);

  return (
    <main data-testid="admin-subscribers" className="mx-auto max-w-3xl px-6 py-12">
      <Link
        to="/admin"
        data-testid="subscribers-back-link"
        className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#F7F1E8]/78 hover:text-[#F7F1E8]"
      >
        <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.6} /> Înapoi la Atelier
      </Link>

      <div className="mt-8 border-b border-[color-mix(in_srgb,var(--milk)_18%,transparent)] pb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-[var(--accent)]">Abonați</p>
        <h1 className="mt-2 font-serif text-4xl font-light text-[#F7F1E8]">Cititorii abonați</h1>
        <p className="mt-2 text-sm text-[#F7F1E8]/78">
          {active.length} {active.length === 1 ? "cititor primește" : "cititori primesc"} fiecare însemnare nouă pe email.
        </p>
      </div>

      {loading ? (
        <p className="py-16 text-center text-sm text-[#F7F1E8]/68">Adun lista&hellip;</p>
      ) : subs.length === 0 ? (
        <div className="mt-10 rounded-3xl bg-[var(--tint-butter)] p-10 text-center" data-testid="subscribers-none">
          <Mail className="mx-auto h-8 w-8 text-[var(--sun-dark)]" strokeWidth={1.4} />
          <p className="mt-4 font-serif text-2xl text-[#F7F1E8]">Niciun abonat încă.</p>
          <p className="mt-2 text-sm text-[#F7F1E8]/78">
            Cititorii se pot abona din formularul aflat în josul fiecărei pagini.
          </p>
        </div>
      ) : (
        <ul className="mt-8 divide-y divide-[color-mix(in_srgb,var(--milk)_18%,transparent)] rounded-2xl border border-[color-mix(in_srgb,var(--milk)_18%,transparent)] bg-[#2B1B13]" data-testid="subscribers-list">
          {subs.map((s) => (
            <li key={s.id} data-testid={`subscriber-${s.email}`} className="flex items-center justify-between gap-4 px-5 py-4">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-[#F7F1E8]">{s.email}</p>
                <p className="mt-0.5 text-[11px] uppercase tracking-[0.15em] text-[#F7F1E8]/62">
                  din {fmt(s.created_at)}
                  {s.active === false && <span className="ml-2 rounded-full bg-[var(--tint-peach)] px-2 py-0.5 text-[#E6414F]">dezabonat</span>}
                </p>
              </div>
              <button
                data-testid={`subscriber-delete-${s.email}`}
                onClick={() => remove(s.id)}
                className="rounded-full p-2 text-[#F7F1E8]/68 transition-colors hover:bg-red-50 hover:text-red-500"
                aria-label={`Șterge ${s.email}`}
              >
                <Trash2 className="h-4 w-4" strokeWidth={1.6} />
              </button>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
};

export default AdminSubscribersPage;
